// src/components/Dashboard/DashboardHeader.tsx
// Hardwired header — always visible, cannot be toggled or removed.
// Panels: AEST clock | ISO date | live weather at the active job site.
// Weather: OpenWeatherMap via VITE_WEATHER_API_KEY env var.
// Falls back to a clearly-labelled mock reading when the key is absent.
//
// FIX: date-fns v2+ restricts the D (day-of-year) format token behind
// useAdditionalDayOfYearTokens. Use getDayOfYear() + isLeapYear() directly
// to avoid the RangeError thrown on every clock tick.
import { useState, useEffect, useCallback } from 'react'
import { format, getDayOfYear, isLeapYear } from 'date-fns'
import { Wind, Droplets, AlertCircle, RefreshCw } from 'lucide-react'
import { MOCK_JOBS } from '../../utils/mockData'

// SEQ centroid — used when no active-site coordinates are available
const SEQ_LAT = -27.4698
const SEQ_LON = 153.0251

// Active site label from the first ACTIVE job
const ACTIVE_JOB = MOCK_JOBS.find(j => j.status === 'ACTIVE')
const SITE_LABEL = ACTIVE_JOB
  ? (ACTIVE_JOB.description ?? '').split('—')[0].trim() || ACTIVE_JOB.jobNumber
  : 'SEQ Region'

// Read env key injected by Vite
const WEATHER_KEY: string = (import.meta as any).env?.VITE_WEATHER_API_KEY ?? ''

interface WeatherReading {
  temp:        number
  description: string
  windKph:     number
  rainPct:     number
  stale:       boolean
  updatedAt:   Date
}

function mockReading(): WeatherReading {
  return {
    temp:        22,
    description: 'Partly cloudy',
    windKph:     18,
    rainPct:     10,
    stale:       true,
    updatedAt:   new Date(),
  }
}

async function fetchWeather(): Promise<WeatherReading> {
  if (!WEATHER_KEY) return mockReading()

  const url =
    `https://api.openweathermap.org/data/2.5/weather` +
    `?lat=${SEQ_LAT}&lon=${SEQ_LON}&appid=${WEATHER_KEY}&units=metric`

  const res = await fetch(url)
  if (!res.ok) throw new Error(`OWM ${res.status}`)
  const d = await res.json()

  return {
    temp:        Math.round(d.main?.temp ?? 22),
    description: d.weather?.[0]?.description ?? 'Clear',
    windKph:     Math.round((d.wind?.speed ?? 0) * 3.6),
    rainPct:     Math.round((d.pop ?? 0) * 100),
    stale:       false,
    updatedAt:   new Date(),
  }
}

export function DashboardHeader() {
  const [now,     setNow]     = useState(new Date())
  const [weather, setWeather] = useState<WeatherReading | null>(null)
  const [loading, setLoading] = useState(false)

  // Live clock — ticks every second
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000)
    return () => clearInterval(id)
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    try   { setWeather(await fetchWeather()) }
    catch { setWeather(mockReading())         }
    finally { setLoading(false) }
  }, [])

  // Initial load + 15-minute auto-refresh
  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 15 * 60 * 1_000)
    return () => clearInterval(id)
  }, [refresh])

  // Day-of-year helpers — avoids the restricted D token in date-fns v2+
  const dayOfYear   = getDayOfYear(now)
  const daysInYear  = isLeapYear(now) ? 366 : 365

  return (
    <div className="bg-[#1F3864] rounded-xl overflow-hidden mb-6 select-none">
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">

        {/* ── Time ─────────────────────────────────────────────── */}
        <div className="px-6 py-4">
          <p className="text-[10px] text-blue-300 uppercase tracking-widest font-semibold mb-1">
            Current time
          </p>
          <p className="text-3xl font-bold text-white tabular-nums tracking-tight">
            {format(now, 'hh:mm')}
            <span className="text-lg font-medium ml-1">{format(now, 'ss')}</span>
            <span className="text-base font-normal ml-2">{format(now, 'a')}</span>
          </p>
          <p className="text-xs text-blue-300 mt-1">AEST (UTC+10)</p>
        </div>

        {/* ── Date ─────────────────────────────────────────────── */}
        <div className="px-6 py-4">
          <p className="text-[10px] text-blue-300 uppercase tracking-widest font-semibold mb-1">
            Today
          </p>
          <p className="text-2xl font-bold text-white leading-tight">
            {format(now, 'EEEE')}
          </p>
          <p className="text-sm text-blue-200 mt-0.5">{format(now, 'd MMMM yyyy')}</p>
          <p className="text-xs text-blue-300 mt-1">
            {format(now, "'Week' w")} · Day {dayOfYear} of {daysInYear}
          </p>
        </div>

        {/* ── Weather ──────────────────────────────────────────── */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] text-blue-300 uppercase tracking-widest font-semibold">
              Site weather
            </p>
            <button
              onClick={refresh}
              disabled={loading}
              title="Refresh weather"
              className="text-blue-400 hover:text-white transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {weather ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-white">{weather.temp}°C</span>
                <span className="text-sm text-blue-200 capitalize">{weather.description}</span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-blue-300">
                <span className="flex items-center gap-1">
                  <Wind className="w-3 h-3" />{weather.windKph} km/h
                </span>
                <span className="flex items-center gap-1">
                  <Droplets className="w-3 h-3" />{weather.rainPct}% rain
                </span>
              </div>
              <p className="text-xs mt-1 truncate">
                {weather.stale ? (
                  <span className="flex items-center gap-1 text-amber-300">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    Mock data · set VITE_WEATHER_API_KEY for live
                  </span>
                ) : (
                  <span className="text-blue-400">
                    {SITE_LABEL} · updated {format(weather.updatedAt, 'h:mm a')}
                  </span>
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-blue-300 animate-pulse mt-1">Loading…</p>
          )}
        </div>

      </div>
    </div>
  )
}
